<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Roll-Call</h5>
        <p class="page-sub">Live list of everyone currently on site — for emergency headcounts and evacuation drills</p>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        <button class="btn-ghost" @click="print"><font-awesome-icon icon="print" /> Print</button>
        <button class="btn-ghost" @click="load"><font-awesome-icon icon="sync-alt" :spin="loading" /> Refresh</button>
        <button class="btn-danger" @click="evacuationPrompt = true"><font-awesome-icon icon="exclamation-triangle" /> Start Evacuation</button>
      </div>
    </div>

    <!-- Offline banner -->
    <div v-if="isOffline" class="offline-banner">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 1l22 22M16.72 11.06A10.94 10.94 0 0119 12.55M5 12.55a10.94 10.94 0 015.17-2.39M10.71 5.05A16 16 0 0122.56 9M1.42 9a15.91 15.91 0 014.7-2.88M8.53 16.11a6 6 0 016.95 0M12 20h.01"/></svg>
      You are offline. Showing cached roll-call from <strong>{{ cachedAt }}</strong>. Data may be outdated.
    </div>

    <!-- Summary bar -->
    <div v-if="!loading && data" class="summary-bar">
      <div class="summary-item">
        <span class="summary-num">{{ data.total_on_site }}</span>
        <span class="summary-label">Total on site</span>
      </div>
      <div class="summary-item" v-if="data.by_site?.length">
        <span class="summary-num">{{ data.by_site.length }}</span>
        <span class="summary-label">Sites</span>
      </div>
      <div class="summary-item">
        <span class="summary-label" style="font-size:12px;">Last updated: {{ lastUpdated }}</span>
      </div>
    </div>

    <div v-if="loading" class="sk-table">
      <div v-for="n in 6" :key="n" class="sk-row"></div>
    </div>

    <div v-else-if="data && data.total_on_site === 0" class="empty-state">
      <font-awesome-icon icon="building" class="empty-icon" />
      <p class="empty-title">No visitors on site</p>
      <p class="empty-sub">The roll-call will populate when visitors check in.</p>
    </div>

    <div v-else-if="data" class="roll-content">
      <!-- Unassigned (no site) -->
      <div v-if="data.unassigned?.length" class="site-section">
        <div class="site-section-header">
          <font-awesome-icon icon="building" class="site-icon" />
          <span class="site-section-name">Unassigned Site</span>
          <span class="site-count">{{ data.unassigned.length }}</span>
        </div>
        <div class="visitor-table-wrap">
          <table class="visitor-table">
            <thead>
              <tr><th>Badge</th><th>Visitor</th><th>Company</th><th>Host</th><th>Purpose</th><th>Checked In</th><th>Time on Site</th></tr>
            </thead>
            <tbody>
              <tr v-for="v in data.unassigned" :key="v.id" :class="{ 'row--safe': v.safe_at }">
                <td><span class="badge-chip">{{ v.badge_no ?? '—' }}</span></td>
                <td class="td-bold">
                  <div class="visitor-name-cell">
                    <div class="visitor-av">{{ v.visitor_name?.slice(0,2).toUpperCase() ?? '?' }}</div>
                    {{ v.visitor_name }}
                  </div>
                </td>
                <td class="td-muted">{{ v.visitor_company ?? '—' }}</td>
                <td class="td-muted">{{ v.host_name ?? '—' }}</td>
                <td class="td-muted">{{ v.purpose }}</td>
                <td class="td-muted">{{ formatTime(v.checked_in_at) }}</td>
                <td><span class="duration-badge">{{ elapsed(v.checked_in_at) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- By site -->
      <div v-for="site in data.by_site" :key="site.site_id" class="site-section">
        <div class="site-section-header">
          <font-awesome-icon icon="map-marker-alt" class="site-icon" />
          <span class="site-section-name">{{ site.site_name }}</span>
          <span class="site-count">{{ siteTotal(site) }}</span>
        </div>

        <!-- Visitors not in a zone -->
        <div v-if="site.unzoned?.length" class="zone-block">
          <div class="zone-label">General Area</div>
          <VisitorTableBlock :visitors="site.unzoned" />
        </div>

        <!-- By zone -->
        <div v-for="zone in site.zones" :key="zone.zone_id" class="zone-block">
          <div class="zone-label">
            <font-awesome-icon icon="layer-group" /> {{ zone.zone_name }}
            <span class="zone-count">{{ zone.visitors.length }}</span>
          </div>
          <VisitorTableBlock :visitors="zone.visitors" />
        </div>
      </div>
    </div>

    <!-- Evacuation prompt -->
    <div v-if="evacuationPrompt" class="modal-backdrop" @mousedown.self="evacuationPrompt = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header evac-header">
          <h6 class="modal-title evac-title"><font-awesome-icon icon="exclamation-triangle" /> Start Evacuation</h6>
          <button class="modal-close" @click="evacuationPrompt = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;margin:0 0 16px;">This will initiate an evacuation event. All receptionists and security will see the evacuation roll-call.</p>
          <div class="form-row">
            <label class="form-label">Note (optional)</label>
            <input v-model="evacuationNote" class="form-input" placeholder="e.g. Fire alarm activated — muster at car park" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="evacuationPrompt = false">Cancel</button>
          <button class="btn-danger" @click="startEvacuation" :disabled="startingEvac">
            <font-awesome-icon v-if="startingEvac" icon="spinner" spin />
            {{ startingEvac ? 'Starting…' : 'Confirm Evacuation' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

const VisitorTableBlock = {
  name: 'VisitorTableBlock',
  props: ['visitors'],
  template: `
    <div class="visitor-table-wrap">
      <table class="visitor-table">
        <thead><tr><th>Badge</th><th>Visitor</th><th>Company</th><th>Host</th><th>Purpose</th><th>Checked In</th><th>Duration</th></tr></thead>
        <tbody>
          <tr v-for="v in visitors" :key="v.id" :class="{ 'row--safe': v.safe_at }">
            <td><span class="badge-chip">{{ v.badge_no ?? '—' }}</span></td>
            <td class="td-bold"><div class="visitor-name-cell"><div class="visitor-av">{{ v.visitor_name?.slice(0,2).toUpperCase() ?? '?' }}</div>{{ v.visitor_name }}</div></td>
            <td class="td-muted">{{ v.visitor_company ?? '—' }}</td>
            <td class="td-muted">{{ v.host_name ?? '—' }}</td>
            <td class="td-muted">{{ v.purpose }}</td>
            <td class="td-muted">{{ new Date(v.checked_in_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}) }}</td>
            <td><span class="duration-badge">{{ elapsed(v.checked_in_at) }}</span></td>
          </tr>
        </tbody>
      </table>
    </div>`,
  methods: {
    elapsed(t) {
      const m = Math.floor((Date.now() - new Date(t)) / 60000)
      if (m < 60) return `${m}m`
      return `${Math.floor(m / 60)}h ${m % 60}m`
    },
  },
}

export default {
  name: 'VisitorRollCall',
  components: { VisitorTableBlock },
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true,
      data: null,
      lastUpdated: '—',
      evacuationPrompt: false,
      evacuationNote: '',
      startingEvac: false,
      isOffline: false,
      cachedAt: null,
    }
  },
  created() {
    this._onOnline  = () => { this.isOffline = false; this.load(false) }
    this._onOffline = () => { this.isOffline = true }
    window.addEventListener('online',  this._onOnline)
    window.addEventListener('offline', this._onOffline)
    this.isOffline = !navigator.onLine
    // Try loading from cache first so page renders instantly
    const cached = localStorage.getItem('vms_rollcall_cache')
    if (cached) {
      try {
        const parsed = JSON.parse(cached)
        this.data       = parsed.data
        this.cachedAt   = parsed.cachedAt
        this.lastUpdated = parsed.cachedAt
        this.loading    = false
      } catch { /* ignore corrupt cache */ }
    }
    this.load()
    this._timer = setInterval(() => { if (!this.isOffline) this.load(false) }, 30000)
  },
  beforeUnmount() {
    clearInterval(this._timer)
    window.removeEventListener('online',  this._onOnline)
    window.removeEventListener('offline', this._onOffline)
  },
  methods: {
    async load(showLoading = true) {
      if (this.isOffline) return    // do not attempt fetch when offline
      if (showLoading && !this.data) this.loading = true
      try {
        const r = await axios.get('visitors/roll-call')
        this.data = r.data.data ?? r.data
        const ts = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
        this.lastUpdated = ts
        this.cachedAt = ts
        // Persist to localStorage for offline use
        try {
          localStorage.setItem('vms_rollcall_cache', JSON.stringify({ data: this.data, cachedAt: ts }))
        } catch { /* storage full — ignore */ }
      } catch (e) {
        if (!this.data) this.toast.error('Failed to load roll-call.')
        // If we have cached data, silently fall back to it
      } finally { this.loading = false }
    },
    siteTotal(site) {
      return (site.unzoned?.length ?? 0) + (site.zones?.reduce((s, z) => s + z.visitors.length, 0) ?? 0)
    },
    formatTime(t) { return t ? new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—' },
    elapsed(t) {
      const m = Math.floor((Date.now() - new Date(t)) / 60000)
      if (m < 60) return `${m}m`
      return `${Math.floor(m / 60)}h ${m % 60}m`
    },
    print() { window.print() },
    async startEvacuation() {
      this.startingEvac = true
      try {
        await axios.post('visitors/evacuation/start', { note: this.evacuationNote || null })
        this.toast.success('Evacuation started.')
        this.evacuationPrompt = false
        this.$router.push({ name: 'Visitor Evacuation' })
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to start evacuation.') }
      finally { this.startingEvac = false }
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.btn-ghost   { display:inline-flex; align-items:center; gap:6px; height:38px; padding:0 16px; border:1px solid #dde5f0; border-radius:8px; background:#fff; font-size:13px; color:#3d5068; cursor:pointer; font-family:inherit; }
.btn-ghost:hover { background:#f4f7fb; }
.btn-danger  { display:inline-flex; align-items:center; gap:6px; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }

.summary-bar  { display:flex; align-items:center; gap:24px; background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:14px 20px; margin-bottom:20px; flex-wrap:wrap; }
.summary-item { display:flex; flex-direction:column; gap:2px; }
.summary-num  { font-size:22px; font-weight:800; color:#004AAD; }
.summary-label{ font-size:12px; color:#7a90b0; font-weight:500; }

.roll-content { display:flex; flex-direction:column; gap:20px; }
.site-section { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.site-section-header { display:flex; align-items:center; gap:10px; padding:12px 18px; background:#f4f7fb; border-bottom:1px solid #e4eaf2; }
.site-icon     { color:#7a90b0; }
.site-section-name { font-size:14px; font-weight:700; color:#1a2b45; flex:1; }
.site-count    { background:#004AAD; color:#fff; font-size:11px; font-weight:700; padding:2px 8px; border-radius:20px; }

.zone-block  { border-bottom:1px solid #f0f4f8; }
.zone-block:last-child { border-bottom:none; }
.zone-label  { display:flex; align-items:center; gap:6px; padding:8px 18px; font-size:12px; font-weight:700; color:#7a90b0; background:#fafbfd; text-transform:uppercase; letter-spacing:.04em; border-bottom:1px solid #f0f4f8; }
.zone-count  { background:#e4eaf2; color:#4a5e78; font-size:10px; font-weight:700; padding:1px 6px; border-radius:20px; margin-left:4px; }

.visitor-table-wrap { overflow-x:auto; }
.visitor-table { width:100%; border-collapse:collapse; }
.visitor-table th { padding:9px 14px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; text-align:left; background:#f8fafb; border-bottom:1px solid #e4eaf2; }
.visitor-table td { padding:10px 14px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; }
.visitor-table tr:last-child td { border-bottom:none; }
.visitor-table tr.row--safe td { background:#f0fdf4; }

.visitor-name-cell { display:flex; align-items:center; gap:8px; }
.visitor-av { width:28px; height:28px; border-radius:50%; background:#004AAD; color:#fff; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; }
.td-bold   { font-weight:600; }
.td-muted  { color:#7a90b0; }
.badge-chip { background:#e8eef8; color:#004AAD; font-size:11px; font-weight:700; padding:3px 8px; border-radius:6px; font-family:monospace; }
.duration-badge { background:#f1f5f9; color:#64748b; font-size:11px; font-weight:700; padding:3px 8px; border-radius:20px; }

.empty-state { text-align:center; padding:60px 20px; }
.empty-icon  { font-size:48px; color:#dde5f0; margin-bottom:16px; }
.empty-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0; }

.modal-backdrop { position:fixed; inset:0; background:rgba(10,20,40,.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
.modal-box { background:#fff; border-radius:16px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm { max-width:420px; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px 14px; border-bottom:1px solid #f0f4f8; }
.evac-header  { background:#fff5f5; }
.modal-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.evac-title   { color:#dc2626; }
.modal-close  { background:none; border:none; font-size:16px; color:#7a90b0; cursor:pointer; padding:4px; }
.modal-body   { padding:22px; }
.modal-footer { padding:14px 22px; border-top:1px solid #f0f4f8; display:flex; justify-content:flex-end; gap:10px; }
.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#dc2626; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.offline-banner { display:flex; align-items:center; gap:10px; background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:12px 16px; font-size:13px; color:#92400e; margin-bottom:16px; }

@media print {
  .page-header button, .summary-bar .summary-label:last-child { display:none; }
}
</style>
